import React, { useState } from 'react';
import { ProFeedbackDisplayProps, AIAnalysisResponse } from '../types';
import { analyzeResults } from '../services/api';
import { useProAnalysis } from '../hooks/useProAnalysis';
import { OverviewTab, PhonemesTab, TimingTab, AICoachTab, TabType } from './pro-feedback';

const ProFeedbackDisplay: React.FC<ProFeedbackDisplayProps> = ({
  targetText,
  transcriptionResult,
  proAnalysis,
  isPlaying,
  onPlayRecording,
  onStopPlayback,
  hasRecording,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [aiAnalysis, setAiAnalysis] = useState<AIAnalysisResponse | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const {
    analyzedWords,
    stats,
    overallScore,
    avgConfidence,
    timingAnalysis,
    azureAnalysis,
    phonemesLoading,
    getWordPhonemesDisplay
  } = useProAnalysis({
    targetText,
    transcriptionResult,
    proAnalysis
  });

  if (!transcriptionResult) return null;

  const handleGetAIAnalysis = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const result = await analyzeResults({
        targetText,
        transcribedText: transcriptionResult.text,
        words: analyzedWords.map(w => ({
          word: w.word,
          status: w.status,
          confidence: w.confidence,
          transcribed: w.transcribed
        })),
        overallScore,
        wordsPerMinute: timingAnalysis?.wordsPerMinute,
        fluencyScore: timingAnalysis?.fluencyScore
      });
      setAiAnalysis(result);
    } catch (err) {
      console.error('AI analysis error:', err);
      setAiError('Failed to get AI analysis. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const tabProps = {
    analyzedWords,
    stats,
    overallScore,
    avgConfidence,
    timingAnalysis,
    azureAnalysis,
    transcriptionText: transcriptionResult.text,
    targetText,
    getWordPhonemesDisplay,
    phonemesLoading,
  };

  return (
    <div className="mt-8 bg-white dark:bg-gray-800 rounded-xl shadow-lg">
      {/* Header with scores */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white rounded-t-xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold">Pro Analysis Results</h3>
              {hasRecording && onPlayRecording && onStopPlayback && (
                <button
                  onClick={isPlaying ? onStopPlayback : onPlayRecording}
                  className="px-3 py-1.5 rounded-lg transition-colors flex items-center gap-1.5 text-sm font-medium bg-white/20 hover:bg-white/30 text-white"
                >
                  {isPlaying ? (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                      </svg>
                      Stop
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      Listen
                    </>
                  )}
                </button>
              )}
            </div>
            <p className="text-purple-100 text-sm">
              {azureAnalysis ? 'Detailed phoneme-level feedback' : 'Detailed pronunciation breakdown'}
            </p>
          </div>
          <div className="text-right">
            <div className="text-4xl font-bold">{overallScore}%</div>
            <div className="text-purple-100 text-sm">Overall Score</div>
          </div>
        </div>

        {/* Score breakdown */}
        {azureAnalysis ? (
          <div className="mt-4 grid grid-cols-4 gap-2">
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{azureAnalysis.accuracy}%</div>
              <div className="text-xs text-purple-100">Accuracy</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{azureAnalysis.fluency}%</div>
              <div className="text-xs text-purple-100">Fluency</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{azureAnalysis.completeness}%</div>
              <div className="text-xs text-purple-100">Completeness</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{azureAnalysis.prosodyScore ?? '-'}%</div>
              <div className="text-xs text-purple-100">Prosody</div>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-3 md:grid-cols-6 gap-2">
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{avgConfidence}%</div>
              <div className="text-xs text-purple-100">Avg Conf</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{stats.correct}</div>
              <div className="text-xs text-purple-100">Correct</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{stats.unclear}</div>
              <div className="text-xs text-purple-100">Unclear</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{stats.substitutions}</div>
              <div className="text-xs text-purple-100">Subst.</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{stats.omissions}</div>
              <div className="text-xs text-purple-100">Omitted</div>
            </div>
            <div className="text-center p-2 bg-white/10 rounded-lg">
              <div className="text-xl font-bold">{stats.insertions}</div>
              <div className="text-xs text-purple-100">Extra</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <div className="flex">
          {(['overview', 'phonemes', 'timing', 'ai-coach'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                flex-1 py-3 text-sm font-medium capitalize transition-colors
                ${activeTab === tab
                  ? 'text-purple-600 dark:text-purple-400 border-b-2 border-purple-600 dark:border-purple-400 bg-purple-50 dark:bg-purple-900/30'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }
              `}
            >
              {tab === 'phonemes' ? 'Phoneme Analysis' :
               tab === 'timing' ? 'Timing & Pace' :
               tab === 'ai-coach' ? 'AI Coach' : 'Overview'}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {activeTab === 'overview' && <OverviewTab {...tabProps} />}
        {activeTab === 'phonemes' && <PhonemesTab {...tabProps} />}
        {activeTab === 'timing' && <TimingTab {...tabProps} />}
        {activeTab === 'ai-coach' && (
          <AICoachTab
            {...tabProps}
            aiAnalysis={aiAnalysis}
            aiLoading={aiLoading}
            aiError={aiError}
            onGetAIAnalysis={handleGetAIAnalysis}
          />
        )}
      </div>

      {/* Legend */}
      <div className="px-6 pb-6">
        <div className="flex flex-wrap gap-4 text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-green-200 dark:bg-green-900/50 rounded border border-green-300 dark:border-green-700"></span>
            <span className="text-gray-600 dark:text-gray-400">Correct (&gt;80% confidence)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-yellow-200 dark:bg-yellow-900/50 rounded border border-yellow-300 dark:border-yellow-700"></span>
            <span className="text-gray-600 dark:text-gray-400">Unclear pronunciation</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 bg-red-200 dark:bg-red-900/50 rounded border border-red-300 dark:border-red-700"></span>
            <span className="text-gray-600 dark:text-gray-400">Incorrect / missed</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProFeedbackDisplay;
